/**
 * Reading the event activity log (#1013, OA-1…OA-4): one `lifecycle_events` row per changed field, tagged with
 * the event (`GET /events/{id}/history`). The Activity tab and the guest drawer's Journey both use this.
 *
 * A row is `{audit_event_id, changed_at, actor: {id, name, kind}, event_type, entity_type, contact_id,
 * contact_name, field, old_label, new_label, source, origin_source, reason, operation_id}`. A move that
 * changes several facts writes several rows under one `operation_id`, adjacent in the response; the viewer
 * shows them as ONE entry per guest (`groupEntries`).
 */
import { humanize } from './historyDisplay';

export const KINDS = {
    move: { label: 'Guest moves', icon: 'arrow-right', tone: 'blue' },
    checkin: { label: 'Check-in', icon: 'badge-check', tone: 'teal' },
    rsvp: { label: 'RSVP', icon: 'user-check', tone: 'emerald' },
    guests: { label: 'Added / removed', icon: 'user-plus', tone: 'violet' },
    event: { label: 'Event', icon: 'calendar-check', tone: 'stone' },
};
export const KIND_KEYS = Object.keys(KINDS);

/** The log's tabs: which of the five kinds a row is, from its `event_type`. */
export function kindOf(item) {
    const type = String(item?.event_type || '');
    if (type.startsWith('event.') || item?.entity_type === 'event') return 'event';
    if (/\.(added|removed)$/.test(type)) return 'guests';
    if (type.endsWith('.rsvp_changed')) return 'rsvp';
    if (type.endsWith('.attendance_changed')) return 'checkin';
    return 'move';
}

/** The effective source: a backfilled row keeps the original in `origin_source`. */
const sourceOf = (item) => (item?.source === 'migration-backfill' ? item?.origin_source : item?.source) || item?.origin_source || 'manual';

export const SOURCES = {
    manual: { label: 'By hand', icon: 'hand', tone: 'stone' },
    auto: { label: 'Automatically', icon: 'zap', tone: 'amber' },
    form: { label: 'Registration form', icon: 'file-text', tone: 'blue' },
    face: { label: 'Face match', icon: 'scan-face', tone: 'teal' },
};

export function sourceMeta(item) {
    const source = String(sourceOf(item));
    if (SOURCES[source]) return SOURCES[source];
    if (/face/.test(source)) return SOURCES.face;
    if (/form|capture/.test(source)) return SOURCES.form;
    if (source === 'manual' || source === 'api' || source === 'user') return SOURCES.manual;
    if (item?.actor?.kind === 'system') return SOURCES.auto;
    return { label: humanize(source), icon: 'zap', tone: 'amber' };
}

/** True for a row nobody did by hand (the "Automatic" count and filter). */
export const isAutomatic = (item) => item?.actor?.kind === 'system' || sourceMeta(item) !== SOURCES.manual;

/** The actor's name; a system actor reads `systemName` ("Nurture" in the log, "Automatically" in the drawer). */
export function actorName(item, systemName = 'Nurture') {
    if (item?.actor?.kind === 'system') return systemName;
    return item?.actor?.name || 'Someone';
}

const has = (value, pattern) => pattern.test(String(value || ''));

/** `{verb, tail}` for "**Actor** verb **Guest**tail". */
export function describeRow(item) {
    const type = String(item?.event_type || '');
    const to = item?.new_label;
    if (type === 'event.started') return { verb: 'started the event', tail: '' };
    if (type === 'event.ended') return { verb: item?.actor?.kind === 'system' ? 'ended the event automatically' : 'ended the event', tail: to ? ` · ${to}` : '' };
    if (type === 'event.reopened') return { verb: 'reopened check-in', tail: '' };
    if (type === 'event.rescheduled') return { verb: 'rescheduled the event', tail: item?.old_label && to ? ` from ${item.old_label} to ${to}` : '' };
    if (type === 'event.updated') return { verb: 'edited the event', tail: item?.field ? ` · ${humanize(item.field)}` : '' };
    if (type === 'event.no_show_triggers_dispatched') return { verb: 'sent no-show campaign triggers', tail: to ? ` for ${to}` : '' };
    if (type.startsWith('event.')) return { verb: humanize(type.slice(6)), tail: '' };
    if (type.endsWith('.added')) return { verb: has(item?.reason, /walk/i) || has(to, /walk/i) ? 'added walk-in' : 'added', tail: '' };
    if (type.endsWith('.removed')) return { verb: 'removed', tail: ' from the event' };
    if (type.endsWith('.rescheduled')) return { verb: 'rescheduled', tail: to ? ` to ${String(to).split(' · ')[0]}` : '' };
    if (type.endsWith('.reconfirm_requested')) return { verb: 'asked to reconfirm', tail: '' };
    if (type.endsWith('.rsvp_changed')) {
        if (has(to, /yes|confirm/i)) return { verb: 'recorded an RSVP for', tail: '' };
        if (has(to, /declin/i)) return { verb: 'recorded a decline for', tail: '' };
        return { verb: 'changed the RSVP for', tail: '' };
    }
    if (type.endsWith('.attendance_changed')) {
        if (has(to, /attend|checked/i)) return { verb: 'checked in', tail: '' };
        if (has(to, /no.?show/i)) return { verb: 'marked no-show:', tail: '' };
        return { verb: 'undid the check-in for', tail: '' };
    }
    if (type.endsWith('.stage_changed') && item?.field === 'invited_at') return { verb: 'invited', tail: '' };
    return { verb: 'moved', tail: '' };
}

const HEADLINE = /\.(added|removed|rescheduled|reconfirm_requested)$/;

/** Adjacent rows of one operation for one guest become one entry (VR3-m1). */
export function groupEntries(items = []) {
    const entries = [];
    items.forEach((item) => {
        const last = entries[entries.length - 1];
        if (last && item.operation_id && last.first.operation_id === item.operation_id
            && (last.first.contact_id || null) === (item.contact_id || null)
            && last.first.entity_type === item.entity_type) {
            last.items.push(item);
            return;
        }
        entries.push({ key: item.audit_event_id || `${entries.length}-${item.changed_at}`, first: item, items: [item] });
    });
    // The rows of one operation share a timestamp, so their order is arbitrary: the row that names the action (added,
    // removed, rescheduled, reconfirm requested) leads the entry, whichever the server listed first.
    entries.forEach((entry) => {
        const lead = entry.items.findIndex((row) => HEADLINE.test(String(row.event_type || '')));
        if (lead > 0) { entry.items.unshift(...entry.items.splice(lead, 1)); entry.first = entry.items[0]; }
    });
    return entries.map((entry) => ({
        ...entry,
        // A "From → To" pair per changed field that has one; a row without labels adds none.
        changes: entry.items.filter((row) => row.old_label || row.new_label).map((row) => ({ field: row.field, from: row.old_label || '', to: row.new_label || '' })),
    }));
}

const dateOf = (iso) => { const date = new Date(iso); return Number.isNaN(date.getTime()) ? null : date; };

export function dayLabel(iso, timeZone) {
    const date = dateOf(iso);
    if (!date) return 'Earlier';
    const opts = { weekday: 'short', day: 'numeric', month: 'short' };
    try { return date.toLocaleDateString('en-GB', timeZone ? { ...opts, timeZone } : opts); } catch { return date.toLocaleDateString('en-GB', opts); }
}

export function timeLabel(iso, timeZone) {
    const date = dateOf(iso);
    if (!date) return '';
    const opts = { hour: '2-digit', minute: '2-digit', hour12: false };
    try { return date.toLocaleTimeString('en-GB', timeZone ? { ...opts, timeZone } : opts); } catch { return date.toLocaleTimeString('en-GB', opts); }
}

// ---- a guest's Journey (the drawer) --------------------------------------------------------------

const FIELD_TITLES = {
    outreach_stage: (to) => (to ? `Moved to ${to}` : 'Moved'),
    rsvp_status: (to) => (to ? `RSVP: ${to}` : 'RSVP changed'),
    attendance_status: (to) => (to ? `Attendance: ${to}` : 'Attendance changed'),
    invited_at: () => 'Invited',
    close_reason: (to) => (to ? `Closed · ${to}` : 'Closed'),
};

/** The title of one Journey entry. */
export function journeyTitle(entry) {
    const first = entry.first;
    const type = String(first.event_type || '');
    if (type.endsWith('.added')) return has(first.reason, /walk/i) ? 'Created at the door' : 'Added to the event';
    if (type.endsWith('.removed')) return 'Removed from the event';
    if (type.endsWith('.rescheduled')) return 'Rescheduled to another event';
    if (type.endsWith('.reconfirm_requested')) return 'Asked to reconfirm';
    const titles = entry.items.map((row) => (FIELD_TITLES[row.field] || (() => `${humanize(row.field || 'change')}${row.new_label ? `: ${row.new_label}` : ''}`))(row.new_label));
    return titles.join(' · ');
}

/** "<time> · by <name> · how": a system actor reads "automatically". */
export function journeyMeta(entry, timeZone) {
    const first = entry.first;
    const source = sourceMeta(first);
    const who = first.actor?.kind === 'system' ? 'Automatically' : `by ${actorName(first)}`;
    const how = source === SOURCES.manual || first.actor?.kind === 'system' ? '' : ` · ${source.label}`;
    const why = first.reason ? ` · ${first.reason}` : '';
    return `${dayLabel(first.changed_at, timeZone)} ${timeLabel(first.changed_at, timeZone)} · ${who}${how}${why}`;
}
